import type { BodyRequest } from '@pba/react-utils';
import type { MetaItemTranslations } from 'models';
import { type ApiClientResponse, apiClient, routes } from 'common/services';
import type { EntityDataObject } from 'models/service';

export type CommunityInvitationChangeRequest = {
  /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
  change_request: {
    kind: 'community_invitation';
    meta?: {
      invitation_email?: string;
      invitation_phone?: string;
    };
    subject_id: string;
    subject_type: 'Community';
    subject_user_id?: string;
  };
  /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
};

type ChangeRequestData = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Keys are defined by Backend API endpoint
  subject_id: string;
};

type ChangeRequestResponse<T> = T extends CommunityInvitationChangeRequest[] ? unknown : EntityDataObject;

type CommunityJoinRequestData = {
  /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
  kind: 'community_request_to_join';
  meta: {
    reason_to_join?: string;
  };
  subject_type: 'Community';
  /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
} & ChangeRequestData;

type ContactUsRequestData = {
  /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
  kind: 'program_request_to_contact';
  meta: {
    invitation_phone: string;
  };
  subject_type: 'Program';
  /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
} & ChangeRequestData;

type ProgramJoinRequestData = {
  /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
  kind: 'program_request_to_join';
  meta: {
    reason_to_join?: string;
  };
  subject_type: 'Program';
  /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
} & ChangeRequestData;

type SingleChangeRequest =
  | CommunityJoinRequestData
  | ContactUsRequestData
  | ProgramJoinRequestData
  | TelehealthRequestData;

type TelehealthRequestData = {
  /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
  kind: 'event_request_to_create';
  meta: {
    html_content: string;
    patient_type: TelehealthTypeRequestData;
    text_content: string;
    visit_date: Date;
    visit_duration: number;
    visit_type: TelehealthTypeRequestData;
    visit_type_option: TelehealthTypeRequestData;
    visit_type_sub_option?: TelehealthTypeRequestData;
  };
  subject_type: 'Community';
  /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
} & ChangeRequestData;

type TelehealthTypeRequestData = {
  name: string;
  translations: MetaItemTranslations;
};

export const changeRequest = async <T extends CommunityInvitationChangeRequest[] | SingleChangeRequest>({
  body,
}: BodyRequest<T>) =>
  apiClient.post<ChangeRequestResponse<T>, ApiClientResponse<ChangeRequestResponse<T>>>(
    routes.changeRequests.post,
    body,
  );
